import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useCreateCurrentAssessmentMutation } from "../../infrastructure/api/patientsApi";
import {
  useGetDoctorsQuery,
  useGetGroupsQuery,
} from "@/features/members-groups/infrastructure/api/membersGroupsApi";
import { useParams } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  getAllowedFutureTimeInputValue,
  getMinimumFutureTimeInputValue,
  isPastOrCurrentDateTime,
  isSameLocalDate,
} from "@/lib/dateUtils";

type Props = {
  open: boolean;
  onClose: () => void;
  assessmentId: string;
};

const getPeriodFromTime = (time: string): "am" | "pm" => {
  const hour = Number(time.split(":")[0]);
  return hour >= 12 ? "pm" : "am";
};

const convertTo24Hour = (time: string, period: "am" | "pm"): string => {
  const parts = time.split(":").map(Number);
  let hours = parts[0];
  const minutes = parts[1];
  if (period === "pm" && hours < 12) hours += 12;
  if (period === "am" && hours === 12) hours = 0;
  if (period === "am" && hours > 12) hours -= 12;
  return `${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")}`;
};

const MIN_APPOINTMENT_DURATION_MINUTES = 30;
const DEFAULT_REPORT_REVIEW_DURATION_MINUTES = 45;

const addMinutesToTime = (time: string, minutes: number): string => {
  const [h, m] = time.split(":").map(Number);
  const total = h * 60 + m + minutes;
  return (
    `${Math.floor(total / 60) % 24}`.padStart(2, "0") +
    ":" +
    `${total % 60}`.padStart(2, "0")
  );
};

const getCurrentTimeStr = (): string => {
  const now = new Date();
  return `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
};

const ScheduleReportReviewDialog: React.FC<Props> = ({
  open,
  onClose,
  assessmentId,
}) => {
  const { t } = useTranslation("patients");
  const days = [
    t("scheduling.days.su"),
    t("scheduling.days.mo"),
    t("scheduling.days.tu"),
    t("scheduling.days.we"),
    t("scheduling.days.th"),
    t("scheduling.days.fr"),
    t("scheduling.days.sa"),
  ];
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(
    new Date(today.getFullYear(), today.getMonth()),
  );
  const [selectedDate, setSelectedDate] = useState<number | null>(null);
  const [doctor, setDoctor] = useState("");
  const [doctorError, setDoctorError] = useState("");
  const [startTime, setStartTime] = useState(getCurrentTimeStr);
  const [startPeriod, setStartPeriod] = useState<"am" | "pm">(() =>
    getPeriodFromTime(getCurrentTimeStr()),
  );
  const [endTime, setEndTime] = useState(() =>
    addMinutesToTime(getCurrentTimeStr(), DEFAULT_REPORT_REVIEW_DURATION_MINUTES),
  );
  const [endPeriod, setEndPeriod] = useState<"am" | "pm">(() =>
    getPeriodFromTime(
      addMinutesToTime(getCurrentTimeStr(), DEFAULT_REPORT_REVIEW_DURATION_MINUTES),
    ),
  );

  const { patientId } = useParams<{ patientId: string }>();

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const firstDay = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const { data: groups = [], isLoading: isLoadingGroups } = useGetGroupsQuery(
    undefined,
    { skip: !patientId || !assessmentId },
  );
  const groupIds = groups.map((group) => group.id);
  const { data: doctorMembers = [], isLoading: isLoadingDoctors } =
    useGetDoctorsQuery(
      { group_ids: groupIds },
      { skip: !patientId || !assessmentId || groupIds.length === 0 },
    );

  const recommendedDoctors = doctorMembers.map((member) => ({
    id: member.id,
    name: member.name,
    phone: member.phone,
    designation: member.role || null,
  }));

  const isLoadingDoctorOptions =
    isLoadingGroups ||
    (recommendedDoctors.length === 0 && isLoadingDoctors);

  const [createAssessment] = useCreateCurrentAssessmentMutation();

  const isCurrentMonth =
    year === today.getFullYear() && month === today.getMonth();
  const selectedCalendarDate =
    selectedDate !== null ? new Date(year, month, selectedDate) : null;
  const isSelectedDateToday =
    selectedCalendarDate !== null && isSameLocalDate(selectedCalendarDate);
  const minimumFutureTime = getMinimumFutureTimeInputValue();

  const handlePrevMonth = () => {
    if (isCurrentMonth) return;
    setCurrentDate(new Date(year, month - 1));
    setSelectedDate(null);
  };

  const handleNextMonth = () => {
    setCurrentDate(new Date(year, month + 1));
    setSelectedDate(null);
  };

  const buildDateTime = (
    day: number,
    time: string,
    period: "am" | "pm",
  ): Date => {
    const [h, m] = time.split(":").map(Number);
    let hours = h;
    if (period === "pm" && h < 12) hours += 12;
    if (period === "am" && h === 12) hours = 0;
    if (period === "am" && h > 12) hours -= 12;
    const d = new Date(year, month, day);
    d.setHours(hours, m, 0, 0);
    return d;
  };

  const buildEndDateTime = (day: number, startDate: Date): Date => {
    const endDate = buildDateTime(day, endTime, endPeriod);
    if (endDate <= startDate) {
      endDate.setDate(endDate.getDate() + 1);
    }
    return endDate;
  };

  const handleSchedule = async () => {
    let hasError = false;

    if (!doctor) {
      setDoctorError(t("scheduling.selectDoctorError"));
      hasError = true;
    }

    if (!selectedDate) {
      toast.error(t("scheduling.selectDateError"));
      hasError = true;
    }

    if (hasError) return;

    const startDate = buildDateTime(selectedDate!, startTime, startPeriod);
    const endDate = buildEndDateTime(selectedDate!, startDate);

    if (isPastOrCurrentDateTime(startDate)) {
      return toast.error(t("scheduling.futureTimeError"));
    }

    if (endDate <= startDate) {
      return toast.error(t("scheduling.endAfterStartError"));
    }

    const durationMinutes = (endDate.getTime() - startDate.getTime()) / 60000;
    if (durationMinutes < MIN_APPOINTMENT_DURATION_MINUTES) {
      return toast.error(
        t("scheduleReportReviewDialog.minDurationError", {
          minutes: MIN_APPOINTMENT_DURATION_MINUTES,
        }),
      );
    }

    try {
      await createAssessment({
        patientId: patientId as string,
        data: {
          status: "RR_SCHEDULED",
          scheduled_start_at: startDate.toISOString(),
          scheduled_end_at: endDate.toISOString(),
          notes: "Scheduled from UI",
          recommended_report_review_doctor_user_ids: [doctor],
        },
      }).unwrap();

      toast.success(t("scheduleReportReviewDialog.success"));
      onClose();
    } catch (err) {
      console.error(err);
      toast.error(t("scheduleReportReviewDialog.error"));
    }
  };

  const renderCalendar = () => {
    const cells: React.ReactNode[] = [];

    for (let i = 0; i < firstDay; i++) {
      cells.push(<div key={`empty-${i}`} />);
    }

    const todayDate =
      year === today.getFullYear() && month === today.getMonth()
        ? today.getDate()
        : null;

    for (let d = 1; d <= totalDays; d++) {
      const isSelected = selectedDate === d;
      const isToday = todayDate === d;
      const isPast = new Date(year, month, d).setHours(23, 59, 59) < Date.now();

      cells.push(
        <button
          key={d}
          onClick={() => {
            if (isPast) return;
            setSelectedDate(d);

            const selected = new Date(year, month, d);
            const start = buildDateTime(d, startTime, startPeriod);
            if (isSameLocalDate(selected) && isPastOrCurrentDateTime(start)) {
              const nextStart = getMinimumFutureTimeInputValue();
              const nextEnd = addMinutesToTime(
                nextStart,
                DEFAULT_REPORT_REVIEW_DURATION_MINUTES,
              );
              setStartTime(nextStart);
              setStartPeriod(getPeriodFromTime(nextStart));
              setEndTime(nextEnd);
              setEndPeriod(getPeriodFromTime(nextEnd));
            }
          }}
          disabled={isPast}
          className={`h-10 rounded-md text-sm ${
            isPast
              ? "text-gray-300 cursor-not-allowed border-none"
              : isSelected
                ? "bg-primary text-white border border-primary cursor-pointer"
                : isToday
                  ? "border border-primary font-semibold cursor-pointer"
                  : "hover:bg-gray-100 cursor-pointer"
          }`}
        >
          {d}
        </button>,
      );
    }

    return cells;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] flex-col overflow-hidden rounded-xl p-0 sm:max-w-[650px]">
        <div className="shrink-0 border-b px-6 pt-5 pb-3">
          <h2 className="text-lg font-semibold">{t("scheduleReportReviewDialog.title")}</h2>
          <p className="text-sm text-gray-500">
            {t("scheduleReportReviewDialog.subtitle")}
          </p>
        </div>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-4">
          <div>
            <div className="flex justify-between mb-3">
              <p className="font-medium">
                {currentDate.toLocaleString("default", { month: "short" })}{" "}
                {year}
              </p>
              <div className="flex gap-2">
                <button onClick={handlePrevMonth} disabled={isCurrentMonth}>
                  <ChevronLeft
                    size={16}
                    className={
                      isCurrentMonth ? "text-gray-300" : "cursor-pointer"
                    }
                  />
                </button>
                <button onClick={handleNextMonth}>
                  <ChevronRight size={16} className="cursor-pointer" />
                </button>
              </div>
            </div>

            <div className="grid grid-cols-7 text-xs text-gray-500 text-center mb-2">
              {days.map((d) => (
                <span key={d}>{d}</span>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1 text-center">
              {renderCalendar()}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("scheduling.startTime")} <span className="text-destructive-text">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="time"
                value={startTime}
                min={isSelectedDateToday ? minimumFutureTime : undefined}
                onChange={(e) => {
                  const val = getAllowedFutureTimeInputValue(
                    selectedCalendarDate,
                    e.target.value,
                  );
                  setStartTime(val);
                  setStartPeriod(getPeriodFromTime(val));
                  const end = addMinutesToTime(
                    val,
                    DEFAULT_REPORT_REVIEW_DURATION_MINUTES,
                  );
                  setEndTime(end);
                  setEndPeriod(getPeriodFromTime(end));
                }}
                className="border rounded-md px-3 py-2 text-sm cursor-pointer"
              />
              <div className="flex border rounded-md overflow-hidden">
                {(["am", "pm"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      const newStart = getAllowedFutureTimeInputValue(
                        selectedCalendarDate,
                        convertTo24Hour(startTime, p),
                      );
                      setStartTime(newStart);
                      setStartPeriod(getPeriodFromTime(newStart));
                      const end = addMinutesToTime(
                        newStart,
                        DEFAULT_REPORT_REVIEW_DURATION_MINUTES,
                      );
                      setEndTime(end);
                      setEndPeriod(getPeriodFromTime(end));
                    }}
                    className={`px-4 py-2 text-sm cursor-pointer ${
                      startPeriod === p
                        ? "bg-primary text-white"
                        : "bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("scheduling.endTime")} <span className="text-destructive-text">*</span>
            </label>
            <div className="flex gap-2">
              <input
                type="time"
                value={endTime}
                min={isSelectedDateToday ? minimumFutureTime : undefined}
                onChange={(e) => {
                  const val = getAllowedFutureTimeInputValue(
                    selectedCalendarDate,
                    e.target.value,
                  );
                  setEndTime(val);
                  setEndPeriod(getPeriodFromTime(val));
                }}
                className="border rounded-md px-3 py-2 text-sm cursor-pointer"
              />
              <div className="flex border rounded-md overflow-hidden">
                {(["am", "pm"] as const).map((p) => (
                  <button
                    key={p}
                    onClick={() => {
                      const newEnd = getAllowedFutureTimeInputValue(
                        selectedCalendarDate,
                        convertTo24Hour(endTime, p),
                      );
                      setEndTime(newEnd);
                      setEndPeriod(getPeriodFromTime(newEnd));
                    }}
                    className={`px-4 py-2 text-sm cursor-pointer ${
                      endPeriod === p ? "bg-primary text-white" : "bg-gray-100"
                    }`}
                  >
                    {p}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div>
            <label className="text-sm font-medium mb-1 block">
              {t("scheduling.doctorName")} <span className="text-destructive-text">*</span>
            </label>
            <Select
              value={doctor}
              onValueChange={(val) => {
                setDoctor(val);
                setDoctorError("");
              }}
            >
              <SelectTrigger
                className={`h-12 w-full cursor-pointer ${doctorError ? "border-destructive" : ""}`}
              >
                <SelectValue placeholder={t("scheduling.selectDoctor")} />
              </SelectTrigger>
              <SelectContent
                position="popper"
                side="bottom"
                align="start"
                className="z-50 py-2 cursor-pointer"
              >
                {isLoadingDoctorOptions ? (
                  <SelectItem value="loading" disabled>
                    {t("scheduling.loading")}
                  </SelectItem>
                ) : recommendedDoctors.length === 0 ? (
                  <SelectItem value="empty" disabled>
                    {t("scheduling.noDoctors")}
                  </SelectItem>
                ) : (
                  recommendedDoctors.map((doc) => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.name}
                      {doc.designation ? ` - ${doc.designation}` : ""}
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
            {doctorError && (
              <p className="text-xs text-destructive-text mt-1">
                {doctorError}
              </p>
            )}
          </div>
        </div>

        <div className="flex shrink-0 justify-end gap-2 border-t px-6 py-4">
          <Button
            variant="outline"
            onClick={onClose}
            className="cursor-pointer px-3 hover:bg-white"
          >
            {t("scheduling.cancel")}
          </Button>
          <Button
            onClick={handleSchedule}
            className="cursor-pointer bg-primary px-3 text-white"
          >
            {t("scheduleReportReviewDialog.schedule")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ScheduleReportReviewDialog;
